/**
 * Pixel Sky — the six background scenes and the renderer that draws them.
 *
 * One file, no imports, on purpose: `scripts/gen-sky.ts` runs it directly under
 * Node's type stripping to write the committed PNGs in `app/public/sky/`, the
 * unit tests render it to prove card contrast, and the shell will read the scene
 * tokens from it. Three consumers, one table — a scene cannot drift between the
 * picture and the colours drawn over it.
 *
 * The noise is a deterministic hash, so a given (scene, theme, size, seed) is the
 * same picture on every machine and every boot. That is what lets the sky be a
 * build artefact instead of a runtime canvas (PIXEL-SKY-DESIGN.md §Sky).
 *
 * Keep it strippable: no enums, namespaces or parameter properties.
 */

export type Rgb = readonly [number, number, number];
export type SkyTheme = "day" | "night";

type Palette = {
  top: Rgb;
  bot: Rgb;
  cloud: Rgb;
  edge: Rgb;
  /** Cloud cover offset; more negative = fewer clouds. */
  bias?: number;
  star?: Rgb;
  /** Star threshold on the hash, closer to 1 = sparser. */
  starD?: number;
  moon?: Rgb;
  sun?: Rgb;
  petal?: Rgb;
  petalD?: number;
  sea?: readonly [Rgb, Rgb];
  foam?: Rgb;
  /** Fraction of the height where the sea starts. */
  horizon?: number;
  hills?: readonly [Rgb, Rgb];
  lights?: Rgb;
  aurora?: readonly [Rgb, Rgb];
};

export type Scene = {
  name: string;
  /** Brand accent for this scene (focus ring, primary button, ticks). */
  accent: string;
  /** Card base colour per theme; drawn at {@link SURFACE_ALPHA} over the sky. */
  surface: { day: Rgb; night: Rgb };
  day: Palette;
  night: Palette;
};

/** Card opacity over the sky. Chosen so white text clears AA over the brightest cloud. */
export const SURFACE_ALPHA = 0.86;

/**
 * The Pixel Sky text ramp: light text on dark translucent cards in both themes
 * (the look is a dark UI over a bright sky). `sky.test.ts` holds every scene to
 * AA for all three steps.
 */
export const TEXT: Record<SkyTheme, { text: Rgb; muted: Rgb; subtle: Rgb }> = {
  day: { text: [255, 255, 255], muted: [227, 234, 234], subtle: [205, 214, 214] },
  night: { text: [255, 255, 255], muted: [214, 222, 234], subtle: [190, 200, 214] },
};

export const SCENES = {
  teal: {
    name: "Teal Sky",
    accent: "#4cdada",
    surface: { day: [60, 72, 74], night: [30, 40, 56] },
    day: { top: [150, 174, 176], bot: [116, 140, 144], cloud: [232, 218, 176], edge: [186, 192, 176] },
    night: { top: [18, 26, 46], bot: [36, 58, 74], cloud: [86, 92, 122], edge: [56, 66, 92], star: [240, 230, 200] },
  },
  sakura: {
    name: "Sakura Dawn",
    accent: "#ff9ec4",
    surface: { day: [87, 56, 73], night: [46, 26, 46] },
    day: { top: [246, 196, 210], bot: [255, 226, 204], cloud: [255, 248, 246], edge: [238, 180, 200], petal: [232, 104, 150], petalD: 0.9978, bias: -0.44 },
    night: { top: [44, 22, 52], bot: [104, 48, 82], cloud: [150, 92, 130], edge: [112, 64, 100], star: [255, 220, 236], petal: [255, 160, 200], petalD: 0.9982, moon: [255, 236, 240] },
  },
  lavender: {
    name: "Lavender Dusk",
    accent: "#b9a2ff",
    surface: { day: [70, 58, 104], night: [30, 24, 58] },
    day: { top: [178, 164, 232], bot: [248, 186, 214], cloud: [255, 236, 244], edge: [222, 196, 236], bias: -0.48 },
    night: { top: [20, 16, 56], bot: [74, 40, 108], cloud: [110, 84, 150], edge: [76, 58, 120], star: [255, 244, 210], starD: 0.985, moon: [255, 246, 214] },
  },
  ocean: {
    name: "Ocean Horizon",
    accent: "#5cc8ff",
    surface: { day: [30, 66, 100], night: [12, 26, 52] },
    day: { top: [120, 192, 238], bot: [200, 232, 248], cloud: [255, 255, 255], edge: [206, 230, 246], sea: [[48, 132, 192], [22, 78, 140]], foam: [214, 240, 255], horizon: 0.62, bias: -0.3 },
    night: { top: [8, 16, 40], bot: [26, 52, 92], cloud: [60, 80, 120], edge: [40, 58, 96], star: [230, 240, 255], sea: [[18, 40, 76], [6, 16, 36]], foam: [70, 110, 160], horizon: 0.62, moon: [250, 248, 226], bias: -0.36 },
  },
  golden: {
    name: "Golden Hills",
    accent: "#ffc062",
    surface: { day: [90, 59, 39], night: [40, 26, 42] },
    day: { top: [255, 196, 120], bot: [255, 228, 178], cloud: [255, 242, 214], edge: [252, 214, 160], hills: [[214, 138, 92], [150, 88, 70]], sun: [255, 246, 214], bias: -0.5 },
    night: { top: [36, 20, 52], bot: [140, 70, 76], cloud: [170, 100, 100], edge: [120, 70, 84], star: [255, 230, 190], hills: [[44, 26, 44], [22, 12, 26]], lights: [255, 206, 120], bias: -0.6 },
  },
  aurora: {
    name: "Mint Aurora",
    accent: "#5ef0c0",
    surface: { day: [30, 71, 66], night: [8, 32, 38] },
    day: { top: [132, 218, 200], bot: [214, 246, 230], cloud: [255, 255, 250], edge: [190, 232, 214], bias: -0.42 },
    night: { top: [4, 18, 30], bot: [12, 44, 56], cloud: [30, 70, 80], edge: [20, 52, 64], star: [220, 255, 240], starD: 0.988, aurora: [[94, 240, 170], [150, 110, 255]], bias: -0.66 },
  },
} satisfies Record<string, Scene>;

export type SceneKey = keyof typeof SCENES;
export const SCENE_KEYS = Object.keys(SCENES) as SceneKey[];

/** The committed sky images are this size; CSS scales them up with `image-rendering: pixelated`. */
export const SKY_SIZE = { width: 320, height: 200 } as const;

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

function hash(a: number, b: number): number {
  const s = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return s - Math.floor(s);
}
function smooth(a: number, b: number): number {
  const ia = Math.floor(a), ib = Math.floor(b), fa = a - ia, fb = b - ib;
  const u = fa * fa * (3 - 2 * fa), v = fb * fb * (3 - 2 * fb);
  return hash(ia, ib) * (1 - u) * (1 - v) + hash(ia + 1, ib) * u * (1 - v) + hash(ia, ib + 1) * (1 - u) * v + hash(ia + 1, ib + 1) * u * v;
}
function fbm(a: number, b: number): number {
  let t = 0, amp = 0.5, f = 1;
  for (let i = 0; i < 4; i++) { t += amp * smooth(a * f, b * f); amp /= 2; f *= 2; }
  return t;
}
const BAYER = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** Draws one scene into an RGBA buffer (row-major, 4 bytes per pixel). */
export function renderSky(key: SceneKey, theme: SkyTheme, width: number = SKY_SIZE.width, height: number = SKY_SIZE.height, seed = 0): Uint8ClampedArray {
  const p: Palette = SCENES[key][theme];
  const W = width, H = height, out = new Uint8ClampedArray(W * H * 4);
  const hz = p.horizon ?? 2, mx = W * 0.78, my = H * 0.17, mr = Math.max(5, H * 0.045);
  const sx = W * 0.28, sy = H * 0.6, sr = Math.max(9, H * 0.1);
  for (let y = 0; y < H; y++) {
    for (let X = 0; X < W; X++) {
      const h = y / H, th = (BAYER[y % 4][X % 4] + 0.5) / 16;
      let col: Rgb = mix(p.top, p.bot, Math.min(1, h / Math.min(1, hz)));
      const r1 = 0.7 + 0.08 * fbm((X + seed) / 34, 3.3), r2 = 0.8 + 0.07 * fbm((X + seed) / 20, 7.1);
      const inSea = !!p.sea && h > hz;
      const inHill = !!p.hills && h > r1;
      if (inSea && p.sea && p.foam) {
        const sh = (h - hz) / (1 - hz);
        col = mix(p.sea[0], p.sea[1], sh);
        if (smooth((X + seed) / (10 + sh * 6), y * 1.4) > 0.82 - sh * 0.08 && th < 0.4) col = p.foam;
        if (p.moon && Math.abs(X - mx) < 2 + sh * 10 && smooth(X / 3, y * 2) > 0.45 && th < 0.7) col = p.moon;
      } else if (inHill && p.hills) {
        col = h > r2 ? p.hills[1] : p.hills[0];
        if (h > r2 && p.lights && hash(X * 2.3 + seed, y * 5.1) > 0.975) col = p.lights;
      } else {
        const ds = Math.hypot(X - sx, y - sy);
        if (p.sun && ds < sr * 2.4 && th < Math.max(0, 1 - (ds - sr) / (sr * 1.4)) * 0.5) col = mix(col, p.sun, 0.5);
        if (p.sun && ds < sr) col = p.sun;
        const dm = Math.hypot(X - mx, y - my);
        if (p.moon && dm < mr) col = p.moon;
        else if (p.moon && dm < mr * 2.2 && th < (1 - (dm - mr) / (mr * 1.2)) * 0.35) col = mix(col, p.moon, 0.35);
        else {
          if (p.aurora) {
            for (let k = 0; k < 2; k++) {
              const cy = 0.2 + k * 0.14 + 0.07 * Math.sin((X + seed) / 26 + k * 2.1) + 0.03 * Math.sin(X / 9 + k);
              const it = Math.exp(-Math.pow((h - cy) / 0.05, 2)) * (0.45 + 0.55 * smooth(X / 7 + k * 9, k));
              if (it > th) col = mix(col, p.aurora[k], 0.55 + 0.3 * it);
            }
          }
          const n = fbm((X + seed) / 38, y / 26) + h * 0.75 + (p.bias ?? -0.5);
          const cl = n > 0.62 ? 1 : n > 0.5 ? (n - 0.5) / 0.12 : 0;
          if (cl > th) { const k = (1 - cl) * 40; col = [p.cloud[0] - k, p.cloud[1] - k, p.cloud[2] - k]; }
          else if (cl > 0 && cl > th * 0.6) col = p.edge;
          else if (p.star && h < 0.45 && hash(X * 3.1 + seed, y * 7.7) > (p.starD ?? 0.992)) col = p.star;
        }
      }
      if (p.petal && p.petalD && !inSea) {
        // 2×2 petals with one corner bitten off.
        const px = X - (X % 2), py = y - (y % 2);
        if (hash(px * 1.7 + seed * 3, py * 2.9) > p.petalD && !(X % 2 && y % 2)) col = p.petal;
      }
      const i = (y * W + X) * 4;
      out[i] = col[0]; out[i + 1] = col[1]; out[i + 2] = col[2]; out[i + 3] = 255;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Contrast (WCAG 2.x)
// ---------------------------------------------------------------------------

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
export function luminance(c: Rgb): number {
  return 0.2126 * channel(c[0]) + 0.7152 * channel(c[1]) + 0.0722 * channel(c[2]);
}
export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** `top` at `alpha` over `under` — what a translucent card looks like where it sits. */
export function over(top: Rgb, alpha: number, under: Rgb): Rgb {
  return [top[0] * alpha + under[0] * (1 - alpha), top[1] * alpha + under[1] * (1 - alpha), top[2] * alpha + under[2] * (1 - alpha)];
}
